import { Spacing } from '@persona-id/design-tokens';
import { Dispatch, SetStateAction, useMemo } from 'react';
import styled from 'styled-components';

import DiffStatusSticker from 'dashboard/apps/InquiryTemplateEditor/DiffStatusSticker';
import WorkflowStepDiffPanelProperties from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow/WorkflowStepDiffPanel/WorkflowStepDiffPanelProperties';
import {
  VersionDiffChangeType,
  WorkflowVersionStepArrayDiff,
  WorkflowVersionStepDiff,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import Accordion from 'dashboard/components/Accordion';
import { useWorkflowActionMeta } from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider/WorkflowActionMetaProvider';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowActionMeta } from 'dashboard/models/workflow';
import { GenericWorkflowAction, WorkflowSchema } from 'dashboard/models/workflow-schema';
import { WorkflowStep } from 'dashboard/models/workflow-version';
import { assertNever } from 'lib/utils/types';

const WorkflowStepPanel = styled.div`
  width: 45%;
  background: ${({ theme }) => theme.color.background.primary};
  overflow-y: auto;
`;

const Container = styled.div`
  padding: ${Spacing[3]};
  padding-bottom: 0px;
`;

const AccordionTitle = styled.div`
  display: flex;
  justify-content: space-between;
`;

interface WorkflowStepPanelCardProps {
  workflowVersionId: string;
  workflowStep: WorkflowStep;
  workflowStepDiff?: WorkflowVersionStepDiff;
  changeType?: VersionDiffChangeType;
  setActiveNodeToken: Dispatch<SetStateAction<string>>;
  setFocusedNodeToken: Dispatch<SetStateAction<string | null>>;
  focused: boolean;
  stepsConfig: WorkflowStep[];
  trigger: Trigger;
  workflowActionsByKey: Record<GenericWorkflowAction['key'], GenericWorkflowAction>;
  actionMeta: WorkflowActionMeta;
}

const WorkflowStepPanelCard = ({
  workflowVersionId,
  workflowStep,
  workflowStepDiff,
  changeType,
  setActiveNodeToken,
  setFocusedNodeToken,
  focused,
  stepsConfig,
  trigger,
  workflowActionsByKey,
  actionMeta,
}: WorkflowStepPanelCardProps) => {
  return (
    <Container>
      <Accordion
        overrideExpanded={focused}
        defaultExpanded={false}
        onChange={() => {
          setActiveNodeToken(workflowStep.token);
          setFocusedNodeToken(workflowStep.token);
        }}
        onTitleClick={() => {
          setActiveNodeToken(workflowStep.token);
          setFocusedNodeToken(workflowStep.token);
        }}
        caretSize='sm'
        title={
          <AccordionTitle>
            {workflowStep.name}
            {changeType && <DiffStatusSticker changeType={changeType} />}
          </AccordionTitle>
        }
        titleGapSize={Spacing[0]}
      >
        <WorkflowStepDiffPanelProperties
          workflowVersionId={workflowVersionId}
          workflowStepDiff={workflowStepDiff}
          workflowStep={workflowStep}
          stepsConfig={stepsConfig}
          trigger={trigger}
          workflowActionsByKey={workflowActionsByKey}
          actionMeta={actionMeta}
        />
      </Accordion>
    </Container>
  );
};

const WorkflowStepPanelCardFromStepDiff = ({
  workflowVersionId,
  workflowStepDiff,
  showCurrentVersionTree,
  setActiveNodeToken,
  setFocusedNodeToken,
  focused,
  stepsConfig,
  trigger,
  workflowActionsByKey,
  actionMeta,
}: {
  workflowVersionId: string;
  workflowStepDiff: WorkflowVersionStepDiff;
  showCurrentVersionTree: boolean;
  setActiveNodeToken: Dispatch<SetStateAction<string>>;
  setFocusedNodeToken: Dispatch<SetStateAction<string | null>>;
  focused: boolean;
  stepsConfig: WorkflowStep[];
  trigger: Trigger;
  workflowActionsByKey: Record<GenericWorkflowAction['key'], GenericWorkflowAction>;
  actionMeta: WorkflowActionMeta;
}) => {
  const workflowPanelSharedProps: Omit<
    WorkflowStepPanelCardProps,
    'workflowStep' | 'workflowStepDiff'
  > = {
    workflowVersionId,
    changeType: workflowStepDiff['change-type'],
    setActiveNodeToken: setActiveNodeToken,
    setFocusedNodeToken: setFocusedNodeToken,
    stepsConfig,
    trigger,
    workflowActionsByKey,
    actionMeta,
    focused,
  };

  switch (workflowStepDiff['change-type']) {
    case VersionDiffChangeType.MODIFIED:
      return (
        <WorkflowStepPanelCard
          {...workflowPanelSharedProps}
          workflowStep={
            showCurrentVersionTree
              ? workflowStepDiff.meta['target-value']
              : workflowStepDiff.meta['base-value']
          }
          workflowStepDiff={workflowStepDiff}
        />
      );
    case VersionDiffChangeType.ADDED:
      return (
        <>
          {showCurrentVersionTree && (
            <WorkflowStepPanelCard
              {...workflowPanelSharedProps}
              workflowStep={workflowStepDiff.meta['value']}
            />
          )}
        </>
      );
    case VersionDiffChangeType.REMOVED:
      return (
        <>
          {!showCurrentVersionTree && (
            <WorkflowStepPanelCard
              {...workflowPanelSharedProps}
              workflowStep={workflowStepDiff.meta['value']}
            />
          )}
        </>
      );
    default:
      assertNever(workflowStepDiff['change-type']);
  }
};

interface WorkflowStepDiffPanelProps {
  workflowVersionId: string;
  workflowActionSequenceChanges: WorkflowVersionStepArrayDiff;
  showCurrentVersionTree: boolean;
  workflowSchema: WorkflowSchema;
  setActiveNodeToken: Dispatch<SetStateAction<string>>;
  setFocusedNodeToken: Dispatch<SetStateAction<string | null>>;
  focusedNodeToken: string | null;
  flattenedStepsConfig: WorkflowStep[];
  showUnchanged: boolean;
  trigger: Trigger;
}

export const WorkflowDiffStepPanel = ({
  workflowVersionId,
  workflowActionSequenceChanges,
  showCurrentVersionTree,
  workflowSchema,
  setActiveNodeToken,
  setFocusedNodeToken,
  focusedNodeToken,
  flattenedStepsConfig,
  showUnchanged,
  trigger,
}: WorkflowStepDiffPanelProps) => {
  const actionMeta = useWorkflowActionMeta();
  const workflowActionsByKey = useMemo(() => {
    return workflowSchema['generic-workflow-actions'].reduce<
      Record<GenericWorkflowAction['key'], GenericWorkflowAction>
    >(
      (result, action) => ({
        ...result,
        [action.key]: action,
      }),
      {},
    );
  }, [workflowSchema]);

  return (
    <WorkflowStepPanel>
      {flattenedStepsConfig.map((workflowStep, index) => {
        if (workflowStep.token in workflowActionSequenceChanges) {
          const workflowStepDiff = workflowActionSequenceChanges[workflowStep.token];

          return (
            <WorkflowStepPanelCardFromStepDiff
              key={index}
              workflowVersionId={workflowVersionId}
              workflowStepDiff={workflowStepDiff}
              showCurrentVersionTree={showCurrentVersionTree}
              setActiveNodeToken={setActiveNodeToken}
              setFocusedNodeToken={setFocusedNodeToken}
              focused={focusedNodeToken === workflowStep.token}
              stepsConfig={flattenedStepsConfig}
              trigger={trigger}
              workflowActionsByKey={workflowActionsByKey}
              actionMeta={actionMeta}
            />
          );
        } else if (showUnchanged) {
          return (
            <WorkflowStepPanelCard
              key={index}
              workflowVersionId={workflowVersionId}
              setActiveNodeToken={setActiveNodeToken}
              setFocusedNodeToken={setFocusedNodeToken}
              workflowStep={workflowStep}
              focused={focusedNodeToken === workflowStep.token}
              stepsConfig={flattenedStepsConfig}
              trigger={trigger}
              workflowActionsByKey={workflowActionsByKey}
              actionMeta={actionMeta}
            />
          );
        }
      })}
    </WorkflowStepPanel>
  );
};
